import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Label,
  FormGroup,
  Form,
  FormFeedback
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import axios from "axios";
import BASE_URL from "../../../config";
import Select from "react-select";
import { 
  hasEditOrHigherPermission, 
  hasViewOnlyPermission,
  hasPagePermissionWithAdmin
} from "../../../components/Permission/PermissionService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useNavigate, useLocation } from "react-router-dom";

// API Helpers
import { getEmployees, searchEmployees } from "../../../components/Api/GmsEmployee";
import { getBusinesses } from "../../../components/Api/GmsBusiness";
import { getBranches } from "../../../components/Api/GmsBranch";
import { searchCustomers } from "../../../components/Api/GmsCustomer";

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '42px',
    borderRadius: '8px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.1rem rgba(85, 110, 230, 0.25)' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? '#556ee6' : '#adb5bd',
    }
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  }),
};

const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || authUser.user_Id || 1,
    userName: authUser.userName || "System",
    companyId: authUser.companyId || user.user_Company_Id || null,
    branchId: authUser.branchId || user.user_Branch_Id || null,
    employeeId: authUser.employeeId || user.user_EmployeeId || null,
    isAdmin: authUser.isAdmin || false
  };
};

const GmsSoapEntry = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useMemo(() => getUserSession(), []);
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  
  const isEditUser = session.isAdmin || hasEditOrHigherPermission(permissions, '/GmsSoapEntry');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsSoapEntry');

  const [loading, setLoading] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  const [formData, setFormData] = useState({
    soapId: 0,
    businessId: null,
    branchId: null,
    date: new Date().toISOString().split('T')[0],
    customerId: null,
    staffId: null,
    subjective: '',
    objective: '',
    assessment: '',
    plan: ''
  });

  const [validation, setValidation] = useState({});

  useEffect(() => {
    if (!hasPagePermissionWithAdmin(permissions, '/GmsSoapEntry')) {
      navigate("/Dashboard");
    }
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [busRes] = await Promise.all([
        getBusinesses({ business_Active: true })
      ]);

      const busList = busRes.data || busRes || [];
      setBusinesses(busList);

      // Auto-select Company/Branch from session
      let initialBusId = session.companyId ? (typeof session.companyId === 'string' ? parseInt(session.companyId) : session.companyId) : null;
      let initialBrId = session.branchId ? (typeof session.branchId === 'string' ? parseInt(session.branchId) : session.branchId) : null;

      if (initialBusId) {
        await loadBranchesData(initialBusId);
      }

      setFormData(prev => ({
        ...prev,
        businessId: initialBusId,
        branchId: initialBrId
      }));

      // If editing (passed via state)
      if (location.state && location.state.soapData) {
        const editData = location.state.soapData;
        setFormData({
          soapId: editData.soap_Id,
          businessId: editData.business_Id,
          branchId: editData.branch_Id,
          date: editData.date ? new Date(editData.date).toISOString().split('T')[0] : prev.date,
          customerId: { value: editData.customer_Id, label: editData.customerName },
          staffId: { value: editData.staff_Id, label: editData.staffName },
          subjective: editData.subjective || '',
          objective: editData.objective || '',
          assessment: editData.assessment || '',
          plan: editData.plan || ''
        });
      }

    } catch (error) {
      console.error("Error loading initial data", error);
      toast.error("Failed to load setup data");
    } finally {
      setLoading(false);
    }
  };

  const loadBranchesData = async (busId) => {
    try {
      const res = await getBranches({ Business_Id: busId, branch_Active: true });
      const branchList = res.data || res || [];
      setBranches(branchList);
      
      // Load employees for this business/branch
      loadEmployeesData(busId, null); 
    } catch (e) {
      console.error(e);
    }
  };

  const loadEmployeesData = async (busId, brId) => {
    try {
      const res = await getEmployees({ Employee_Business_Id: busId, Employee_Branch_Id: brId });
      const empList = res.data || res || [];
      const options = empList.map(emp => ({
        value: emp.employee_Id,
        label: emp.employee_NameEn
      }));
      setStaffOptions(options);

      // Staff logic:
      // If view only, force selection to session.employeeId
      if (isViewOnly && session.employeeId) {
        const myEmp = options.find(o => o.value === session.employeeId);
        if (myEmp) setFormData(prev => ({ ...prev, staffId: myEmp }));
      } else if (session.employeeId && !formData.staffId) {
        // Default but stay enabled for edit/admin
        const myEmp = options.find(o => o.value === session.employeeId);
        if (myEmp) setFormData(prev => ({ ...prev, staffId: myEmp }));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const searchCustomersManually = async (inputValue) => {
    const originalInput = inputValue ? inputValue.trim() : '';
    if (!originalInput || originalInput.length < 3) {
      setCustomerOptions([]);
      return;
    }

    try {
      setCustomerSearchLoading(true);
      const response = await searchCustomers(originalInput, 1, 20);
      const items = response.items || response.data?.items || response || [];
      const formattedOptions = (Array.isArray(items) ? items : (items.items || [])).map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));

      setCustomerOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const validate = () => {
    const errors = {};
    if (!formData.businessId) errors.businessId = "Required";
    if (!formData.branchId) errors.branchId = "Required";
    if (!formData.customerId) errors.customerId = "Required";
    if (!formData.staffId) errors.staffId = "Required";
    if (!formData.subjective && !formData.objective && !formData.assessment && !formData.plan) {
        errors.content = "At least one SOAP section must be filled";
    }
    setValidation(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
        if (validation.content) toast.warning(validation.content);
        return;
    }

    try {
      setLoading(true);
      const payload = {
        Soap_Id: formData.soapId,
        Business_Id: formData.businessId,
        Branch_Id: formData.branchId,
        Date: formData.date,
        Customer_Id: formData.customerId.value,
        Staff_Id: formData.staffId.value,
        Subjective: formData.subjective,
        Objective: formData.objective,
        Assessment: formData.assessment,
        Plan: formData.plan,
        CreatedBy: session.userId,
        ModifiedBy: session.userId
      };

      const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
      const response = await axios.post(`${BASE_URL}/api/Soap/save`, payload, authHeader);

      if (response.data && response.data.isSuccess) {
        toast.success(formData.soapId > 0 ? "Visit note updated successfully" : "Visit note saved successfully");
        if (formData.soapId === 0) {
          // Reset form for new entry if desired, or just stay
          setFormData(prev => ({
            ...prev,
            customerId: null,
            subjective: '',
            objective: '',
            assessment: '',
            plan: ''
          }));
          setCustomerOptions([]);
        }
      }
    } catch (error) {
      console.error("Error saving SOAP", error);
      toast.error("Failed to save SOAP note");
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="SOAP" breadcrumbItem={formData.soapId > 0 ? "Edit Visit Note" : "New Visit Note"} />
          
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">Visit Note Entry</h4>
                  <Form>
                    <Row className="mb-4">
                      <Col md={4}>
                        <FormGroup>
                          <Label className="fw-semibold">Company <span className="text-danger">*</span></Label>
                          <Select
                            value={businesses.map(b => ({ value: b.business_Id, label: b.business_NameEn })).find(o => o.value === formData.businessId)}
                            onChange={(opt) => {
                              setFormData({ ...formData, businessId: opt?.value, branchId: null });
                              loadBranchesData(opt?.value);
                            }}
                            options={businesses.map(b => ({ value: b.business_Id, label: b.business_NameEn }))}
                            isDisabled={!!session.companyId}
                            styles={selectStyles}
                            placeholder="Select Company"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label className="fw-semibold">Branch <span className="text-danger">*</span></Label>
                          <Select
                            value={branches.map(b => ({ value: b.branch_Id, label: b.branch_NameEn })).find(o => o.value === formData.branchId)}
                            onChange={(opt) => {
                                setFormData({ ...formData, branchId: opt?.value });
                                loadEmployeesData(formData.businessId, opt?.value);
                            }}
                            options={branches.map(b => ({ value: b.branch_Id, label: b.branch_NameEn }))}
                            isDisabled={!!session.branchId || !formData.businessId}
                            styles={selectStyles}
                            placeholder="Select Branch"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label className="fw-semibold">Date <span className="text-danger">*</span></Label>
                          <Input
                            type="date"
                            value={formData.date}
                            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                            className="form-control"
                            style={{ borderRadius: '8px', minHeight: '38px' }}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="mb-5">
                      <Col md={6}>
                        <FormGroup>
                          <Label className="fw-semibold">Customer <span className="text-danger">*</span></Label>
                          <Select
                            value={formData.customerId}
                            onChange={(opt) => setFormData({ ...formData, customerId: opt })}
                            onInputChange={(inputValue, { action }) => {
                              if (action === 'input-change') {
                                if (searchTimeout) clearTimeout(searchTimeout);
                                const timeout = setTimeout(() => {
                                  searchCustomersManually(inputValue);
                                }, 300);
                                setSearchTimeout(timeout);
                              }
                            }}
                            options={customerOptions}
                            isLoading={customerSearchLoading}
                            placeholder="Type to search customer..."
                            styles={selectStyles}
                            filterOption={() => true}
                            isClearable
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label className="fw-semibold">Staff / Therapist <span className="text-danger">*</span></Label>
                          <Select
                            value={formData.staffId}
                            onChange={(opt) => setFormData({ ...formData, staffId: opt })}
                            options={staffOptions}
                            isDisabled={isViewOnly}
                            styles={selectStyles}
                            placeholder="Select Staff"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* SOAP Sections */}
                    <div className="soap-grid">
                      <Row className="g-4">
                        <Col md={6}>
                          <div className="p-3 border rounded">
                            <Label className="fw-bold mb-1">S – Subjective</Label>
                            <small className="d-block text-muted mb-2">Client concerns (e.g. acne, hair fall)</small>
                            <Input
                              type="textarea"
                              rows="5"
                              placeholder="What is the client reporting?"
                              className="form-control"
                              value={formData.subjective}
                              onChange={(e) => setFormData({...formData, subjective: e.target.value})}
                            />
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="p-3 border rounded">
                            <Label className="fw-bold mb-1">O – Objective</Label>
                            <small className="d-block text-muted mb-2">Observations (skin, hair, condition)</small>
                            <Input
                              type="textarea"
                              rows="5"
                              placeholder="What do you observe?"
                              className="form-control"
                              value={formData.objective}
                              onChange={(e) => setFormData({...formData, objective: e.target.value})}
                            />
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="p-3 border rounded">
                            <Label className="fw-bold mb-1">A – Assessment</Label>
                            <small className="d-block text-muted mb-2">Professional conclusion</small>
                            <Input
                              type="textarea"
                              rows="5"
                              placeholder="What is your analysis?"
                              className="form-control"
                              value={formData.assessment}
                              onChange={(e) => setFormData({...formData, assessment: e.target.value})}
                            />
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="p-3 border rounded">
                            <Label className="fw-bold mb-1">P – Plan</Label>
                            <small className="d-block text-muted mb-2">Treatment done or next steps</small>
                            <Input
                              type="textarea"
                              rows="5"
                              placeholder="What are the recommended steps?"
                              className="form-control"
                              value={formData.plan}
                              onChange={(e) => setFormData({...formData, plan: e.target.value})}
                            />
                          </div>
                        </Col>
                      </Row>
                    </div>

                    <div className="mt-5 text-end">
                        <Button
                          color="primary"
                          className="px-5 shadow-sm"
                          onClick={handleSave}
                          disabled={loading}
                        >
                          {loading ? <i className="bx bx-loader bx-spin font-size-18 align-middle me-2"></i> : null}
                          {formData.soapId > 0 ? "Update Note" : "Save SOAP Note"}
                        </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(GmsSoapEntry);
